import { useState, useCallback, useRef } from 'react';
import LLMService from '../services/llmService';

/**
 * Custom Hook for Chat Functionality
 * Manages chat state and LLM integration
 */
export const useChat = (clientType = 'openai', options = {}) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [availableClients, setAvailableClients] = useState([]);
    const [currentClient, setCurrentClient] = useState(clientType);
    
    // Ref to track if component is mounted
    const isMountedRef = useRef(true);
    
    /**
     * Send a message to the LLM
     * @param {string} content - Message content
     * @param {Object} messageOptions - Additional options for the message
     */
    const sendMessage = useCallback(async (content, messageOptions = {}) => {
        if (!content.trim()) {
            console.warn('useChat: Empty message content');
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            console.log('useChat: Sending message', { content, currentClient, messageOptions });
            
            // Add user message to chat
            const userMessage = {
                id: Date.now(),
                role: 'user',
                content: content,
                timestamp: new Date().toISOString()
            };
            
            setMessages(prev => [...prev, userMessage]);
            
            // Prepare conversation context with lightweight chart summaries
            const conversationMessages = [
                ...messages.map(msg => {
                    // If message has chart metadata, create lightweight summary
                    if (msg.chartMetadata && msg.chartMetadata.hasChart) {
                        return {
                            role: msg.role,
                            content: `[Chart generated for ${msg.chartMetadata.symbol} - ${msg.chartMetadata.chartType} (${msg.chartMetadata.period})]`
                        };
                    }
                    // For regular messages, use the content as is
                    return { role: msg.role, content: msg.content };
                }),
                { role: 'user', content: content }
            ];
            
            // Log token savings for chart messages
            const chartMessages = messages.filter(msg => msg.chartMetadata && msg.chartMetadata.hasChart);
            if (chartMessages.length > 0) {
                console.log(`useChat: Found ${chartMessages.length} chart messages, using lightweight summaries to save tokens`);
                console.log('useChat: Chart summaries:', chartMessages.map(msg => msg.chartMetadata));
            }
            
            // Send to LLM
            const response = await LLMService.sendConversation(
                conversationMessages,
                currentClient,
                { ...options, ...messageOptions }
            );
            
            console.log('useChat: Full response from LLM:', response);
            console.log('useChat: Response fields:', Object.keys(response));
            console.log('useChat: Response type:', typeof response);
            console.log('useChat: Response response field:', response.response);
            console.log('useChat: Response content field:', response.response);
            console.log('useChat: Response response length:', response.response?.length);
            console.log('useChat: Response response preview:', response.response?.substring(0, 200));
            
                        // Extract chart data if present in the response using the same logic as ChatInterface.jsx
            // Note: We keep the current response content intact for UI display
            let charts = null;
            let cleanedResponse = response.response;
            
            // Use the exact same chart detection logic as ChatInterface.jsx
            if (response.response && typeof response.response === 'string') {
                try {
                    const chartData = JSON.parse(response.response);
                                         if (chartData.chart_generated_succesfully_by_chart_tool && chartData.chart_data?.image_base64) {
                         charts = [chartData.chart_data];
                         console.log('useChat: ✅ Chart found using ChatInterface logic:', charts);
                         
                         // Keep the original response content - don't clear it!
                         // The current message should display normally in the UI
                         console.log('useChat: Chart detected, keeping original response content for display');
                     }
                } catch (parseError) {
                    // Not JSON or no chart data, leave as is
                    console.log('useChat: Response is not chart JSON, keeping as is');
                }
            }
            
            // Add assistant response to chat
            const assistantMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: cleanedResponse, // Use cleaned response
                timestamp: new Date().toISOString(),
                model: response.model_name,
                clientType: response.client_type,
                usage: response.usage,
                // Store chart metadata instead of full chart data to save memory and tokens
                chartMetadata: charts ? {
                    hasChart: true,
                    symbol: charts[0]?.symbol,
                    chartType: charts[0]?.chart_type,
                    period: charts[0]?.period,
                    success: charts[0]?.success,
                    chartGenerated: true
                } : null
            };
            
            console.log('useChat: Final message object:', assistantMessage);
            console.log('useChat: Chart metadata in message:', assistantMessage.chartMetadata);
            console.log('useChat: Content in message:', assistantMessage.content);
            
            if (isMountedRef.current) {
                setMessages(prev => [...prev, assistantMessage]);
            }
            
            console.log('useChat: Message sent successfully', assistantMessage);
            
        } catch (err) {
            console.error('useChat: Error sending message', err);
            
            if (isMountedRef.current) {
                setError(err.message);
                
                // Add error message to chat
                const errorMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: `Sorry, I encountered an error: ${err.message}`,
                    timestamp: new Date().toISOString(),
                    isError: true
                };
                
                setMessages(prev => [...prev, errorMessage]);
            }
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [messages, currentClient, options]);
    
    /**
     * Clear chat history
     */
    const clearChat = useCallback(() => {
        console.log('useChat: Clearing chat history');
        setMessages([]);
        setError(null);
    }, []);
    
    /**
     * Load available LLM clients
     */
    const loadAvailableClients = useCallback(async () => {
        try {
            console.log('useChat: Loading available clients');
            const clients = await LLMService.getAvailableClients();
            
            if (isMountedRef.current) {
                setAvailableClients(clients.available_clients || []);
                console.log('useChat: Available clients loaded', clients);
            }
        } catch (err) {
            console.error('useChat: Error loading available clients', err);
            if (isMountedRef.current) {
                setError(`Failed to load available clients: ${err.message}`);
            }
        }
    }, []);
    
    /**
     * Switch LLM client
     * @param {string} newClientType - New client type
     */
    const switchClient = useCallback((newClientType) => {
        console.log('useChat: Switching client', { from: currentClient, to: newClientType });
        setCurrentClient(newClientType);
        setError(null);
    }, [currentClient]);
    
    /**
     * Test LLM functionality
     */
    const testChat = useCallback(async () => {
        try {
            console.log('useChat: Testing chat functionality');
            const result = await LLMService.testChat(currentClient);
            console.log('useChat: Test result', result);
            return result;
        } catch (err) {
            console.error('useChat: Test failed', err);
            setError(`Test failed: ${err.message}`);
            throw err;
        }
    }, [currentClient]);
    
    /**
     * Send a message with specific parameters
     * @param {string} content - Message content
     * @param {string} modelName - Specific model name
     * @param {number} maxTokens - Maximum tokens
     * @param {number} temperature - Response temperature
     */
    const sendMessageWithParams = useCallback(async (content, modelName = null, maxTokens = null, temperature = null) => {
        const messageOptions = {};
        if (modelName) messageOptions.model_name = modelName;
        if (maxTokens) messageOptions.max_tokens = maxTokens;
        if (temperature) messageOptions.temperature = temperature;
        
        return await sendMessage(content, messageOptions);
    }, [sendMessage]);
    
    /**
     * Get chat statistics
     */
    const getChatStats = useCallback(() => {
        const userMessages = messages.filter(msg => msg.role === 'user').length;
        const assistantMessages = messages.filter(msg => msg.role === 'assistant').length;
        const errorMessages = messages.filter(msg => msg.isError).length;
        
        return {
            totalMessages: messages.length,
            userMessages,
            assistantMessages,
            errorMessages,
            currentClient,
            isLoading,
            hasError: !!error
        };
    }, [messages, currentClient, isLoading, error]);
    
    /**
     * Export chat history
     */
    const exportChat = useCallback(() => {
        const chatData = {
            messages: messages,
            stats: getChatStats(),
            exportTime: new Date().toISOString(),
            clientType: currentClient
        };
        
        const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('useChat: Chat exported', chatData);
    }, [messages, getChatStats, currentClient]);
    
    // Cleanup on unmount
    const cleanup = useCallback(() => {
        isMountedRef.current = false;
    }, []);
    
    return {
        // State
        messages,
        isLoading,
        error,
        availableClients,
        currentClient,
        
        // Actions
        sendMessage,
        sendMessageWithParams,
        clearChat,
        switchClient,
        testChat,
        loadAvailableClients,
        
        // Utilities
        getChatStats,
        exportChat,
        cleanup
    };
}; 